/**
 * Pure layout + comment-placement helpers for the Article Workbench
 * (design §2.2 / §2.5 / §2.8). No React, no DOM, no data-fetching — all
 * deterministic and unit-tested. The grid solver itself is
 * `stageColumns` from `@norkron/web-review-workbench` (RULE ZERO: we do NOT
 * reimplement the track math); this module owns only the elaborate-specific
 * seams the lib can't: the URL `?show=` codec and the comment→gutter routing.
 */

import type { Kind } from "@norkron/web-review-workbench";
import type { PairingSide, ResourceRole } from "../api/articleUnits";

/** Canonical left→right order — article is the pivot (design §2.2). */
export const KIND_ORDER: readonly Kind[] = ["brief", "article", "research"];

const VALID = new Set<Kind>(KIND_ORDER);

/** DEFAULT shown-set on first open: Article ONLY (user override of the plan's
 *  Brief+Article — design fork #9, resolved to Article-only on open). */
export const DEFAULT_SHOWN: readonly Kind[] = ["article"];

/**
 * Parse the `?show=` query value into a canonical, de-duplicated shown-set.
 * Empty / null / all-invalid → {@link DEFAULT_SHOWN} (never an empty stage —
 * design §2.2 "never silently drop a pane"). Order is canonicalised so the URL
 * and the grid always agree.
 */
export function parseShow(param: string | null | undefined): Kind[] {
  if (param == null) return [...DEFAULT_SHOWN];
  const wanted = new Set<Kind>();
  for (const raw of param.split(",")) {
    const k = raw.trim() as Kind;
    if (VALID.has(k)) wanted.add(k);
  }
  const shown = KIND_ORDER.filter((k) => wanted.has(k));
  return shown.length > 0 ? shown : [...DEFAULT_SHOWN];
}

/** Serialize a shown-set back to the canonical `?show=` value (stable order). */
export function serializeShow(shown: readonly Kind[]): string {
  const set = new Set(shown);
  return KIND_ORDER.filter((k) => set.has(k)).join(",");
}

/** The two routing columns a comment carries (design §1.7). */
export interface CommentRouting {
  home_resource: ResourceRole;
  pairing_side: PairingSide;
}

/**
 * The design §2.5 placement predicate: does a comment belong in the gutter with
 * this `pairing`? Pure function of the comment's two routing columns
 * (`home_resource`, `pairing_side`) and the gutter identity — so a comment is
 * never double-counted and `pairing_side` disambiguates article-homed notes.
 *
 * ```
 * brief-article    : home∈{brief} OR (home=article AND pairing∈{brief,none})
 * article-research : home∈{research} OR (home=article AND pairing=research)
 * brief-research   : home≠article (both endpoints visible, article hidden)
 * solo:<k>         : home=k
 * ```
 */
export function commentBelongsInGutter(
  c: CommentRouting,
  pairing: string,
): boolean {
  if (pairing.startsWith("solo:")) {
    return c.home_resource === (pairing.slice("solo:".length) as ResourceRole);
  }
  switch (pairing) {
    case "brief-article":
      return (
        c.home_resource === "brief" ||
        (c.home_resource === "article" &&
          (c.pairing_side === "brief" || c.pairing_side === "none"))
      );
    case "article-research":
      return (
        c.home_resource === "research" ||
        (c.home_resource === "article" && c.pairing_side === "research")
      );
    case "brief-research":
      return c.home_resource !== "article";
    default:
      return false;
  }
}

/**
 * The counterpart-chip label for a comment (design §2.5 — "↔ Brief" / "↔
 * Research"): the OTHER endpoint of the pairing the comment addresses, or null
 * for a single-resource note. Drives the chip that scrolls the other pane.
 */
export function counterpartLabel(c: CommentRouting): string | null {
  if (c.home_resource === "article") {
    if (c.pairing_side === "brief") return "Brief";
    if (c.pairing_side === "research") return "Research";
    return null;
  }
  // brief/research-homed comments point back at the article.
  return "Article";
}

/** Title-case a resource role for a header / pill label. */
export function roleTitle(role: Kind): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}
